import type * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

import type * as models from '../../../../models'
import type * as currentRendering from '../../feature'

import type { Feature } from './feature'

export class Selectors {
  /**
   * Represents all widgets on the current page. Primarily, used to determine
   * what widgets need to be rendered
   *
   * When there are multiple segments, this is sorted such that the current
   * segment's widgets are first in the list, since those should be rendered
   * first
   */
  public readonly allWidgetRefs
  /**
   * __NOTE:__ this is only the renderings that are actually available in
   * {@link renderingLookup}. This does not necessarily correspond to
   * {@link allWidgetRefs}
   */
  public readonly allWidgetRenderings
  /** TODO: */
  public readonly renderingLookup
  /** TODO:  */
  public readonly getEntry
  /** TODO: */
  public readonly getRendering
  /** TODO:  */
  public readonly isLoading
  /** TODO:  */
  public readonly getError
  /**
   * Retrieves all {@link models.widget.Ref} that are watching the given
   * {@link ents.core.common.FlexibleIdentifier}; i.e., should be re-rendered after that
   * action executes
   *
   * @see {@link models.widget.Rendering.watch}
   */
  public readonly getRefsWatching

  /**
   * Returns the {@link models.widget.Ref} that corresponds to the given
   * {@link models.widget.Ref.Id}
   */
  public readonly getWidgetRef

  constructor(
    feature: Feature,
    currentRenderingFeature: currentRendering.Feature,
  ) {
    this.allWidgetRefs = currentRenderingFeature.selectors.containers.project(
      // NOTE: The current container dependency would cause equality checks to
      // fail, leading to weird behavior, e.g. the widgets will re-render when
      // segment changes.
      containers =>
        containers.flatMap(container => container?.allWidgetRefs ?? []),
    )

    this.renderingLookup = feature.state.project(state => state.lookup)

    this.allWidgetRenderings = this.renderingLookup.project(lookup =>
      Object.values(lookup)
        .map(entry => entry?.rendering)
        .filter(TypeChecker.isNotNullish.raw),
    )

    this.getEntry = feature.createSelectorMethod(
      ({ id }: Pick<models.widget.Ref, 'id'>) =>
        this.renderingLookup.project(lookup => lookup[id]),
    )
    this.getRendering = feature.createSelectorMethod(
      (ref: Pick<models.widget.Ref, 'id'>) =>
        this.getEntry(ref).project(entry => entry?.rendering),
    )
    this.isLoading = feature.createSelectorMethod(
      (ref: Pick<models.widget.Ref, 'id'>) =>
        this.getEntry(ref).project(entry => entry?.isLoading),
    )
    this.getError = feature.createSelectorMethod(
      (ref: Pick<models.widget.Ref, 'id'>) =>
        this.getEntry(ref).project(entry => entry?.error),
    )

    this.getRefsWatching = feature.createSelectorMethod(
      ({
        alias,
        id,
      }: ents.core.common.FlexibleIdentifier<ents.core.mosaic.Action.Id>) =>
        this.renderingLookup.project(
          this.allWidgetRefs.asSelector,
          (lookup, allWidgetRefs) =>
            Object.entries(lookup).flatMap(([refId, lookupItem]) =>
              lookupItem?.rendering?.watch?.some(
                watching => watching.id === id || watching.alias === alias,
              )
                ? (allWidgetRefs.find(ref => ref.id === refId) ?? [])
                : [],
            ),
        ).asSelector,
    )

    this.getWidgetRef = feature.createSelectorMethod(
      (refId: models.widget.Ref.Id) =>
        this.allWidgetRefs.project(allWidgetRefs =>
          allWidgetRefs.find(widgetRef => widgetRef.id === refId),
        ),
    )
  }
}
